import React, { useState, useEffect } from 'react'
import {
    Toolbar,
    Button,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core'
import { useGlobalStyles } from '../../../App'
import { userService } from '../../../services/user.service'
import accessLevelService from '../../../services/accessLevel.service'
import TableContentLoadingSpinner from '../../shared/TableContentLoadingSpinner'
import { CreateAccessLevelModal } from './CreateAccessLevelModal'
import { EditAccessLevelModal } from './EditAccessLevelModal'

const useStyles = makeStyles(theme => ({
    table: {
        minWidth: 750,
    },
    tableWrapper: {
        padding: theme.spacing(2),
    },
    enhancedTableToolbar: {
        width: '100%',

        '& .MuiToolbar-gutters': {
            paddingRight: 16,
        },
    },
    toolBar: {
        float: 'right',
    },
    tableSection: {
        marginTop: 25,
    },
    formControl: {
        minWidth: 120,
        maxWidth: 300,
    },
    toolbarButtons: {
        marginLeft: 'auto',
    },
}))

const headCells = [
    { id: 'accessLevelId', label: 'APIIA Access Level' },
    { id: 'processEngineAccessLevelId', label: 'ProcessEngine Access Level' },
    { id: 'name', label: 'Name' },
    { id: 'isEnabled', label: 'Is Enabled' },
]

const AccessLevels = () => {
    const classes = useStyles()
    const globalClasses = useGlobalStyles()
    const [tableData, setTableData] = useState()
    const [isLoadingData, setIsLoadingData] = useState(true)
    const managedService = userService.getManagedService()

    const [
        createAccessLevelModalOpen,
        setCreateAccessLevelModalOpen,
    ] = useState(false)

    const createAccessLevelModalOkClickHandler = formData => {
        ;(async function createAccessLevelAsync() {
            try {
                await accessLevelService
                    .createAccessLevel({
                        processEngineServiceId: managedService.id,
                        title: formData.title,
                        isEnabled: formData.isEnabled,
                    })
                    .then(() => {
                        updateTableData()
                    })
            } catch (exception) {
                console.log(exception)
            }
        })()
    }

    const [selectedAccessLevel, setSelectedAccessLevel] = useState()
    const [editAccessLevelModalOpen, setEditAccessLevelModalOpen] = useState(
        false
    )

    const editAccessLevelOkClickHandler = formData => {
        ;(async function updateAccessLevelAsync() {
            try {
                await accessLevelService
                    .updateAccessLevel({
                        processEngineServiceId: managedService.id,
                        serviceAccessLevelId: formData.serviceAccessLevelId,
                        title: formData.title,
                        isEnabled: formData.isEnabled,
                    })
                    .then(() => {
                        updateTableData()
                    })
            } catch (exception) {
                console.log(exception)
            }
        })()
    }

    const updateTableData = () => {
        ;(async function getServiceAsync() {
            try {
                setIsLoadingData(true)
                await accessLevelService
                    .getAllAccessLevels(managedService.id)
                    .then(result => {
                        setTableData(result)
                    })
            } catch (exception) {
                console.log(exception)
            } finally {
                setIsLoadingData(false)
            }
        })()
    }

    useEffect(() => {
        ;(async function updatePageTableDataAsync() {
            try {
                updateTableData()
            } catch (exception) {
                console.log(exception)
            }
        })()
    }, [managedService.id])

    const EnhancedTableToolbar = () => {
        return (
            <div className={classes.enhancedTableToolbar}>
                <Toolbar>
                    <Button
                        className={classes.toolbarButtons}
                        data-testid='add-button'
                        onClick={() => setCreateAccessLevelModalOpen(true)}
                        variant='contained'
                        color='secondary'>
                        Add
                    </Button>
                </Toolbar>
            </div>
        )
    }

    const getTableContent = () => {
        if (isLoadingData) {
            return <TableContentLoadingSpinner />
        }

        return tableData.map(row => (
            <TableRow>
                <TableCell>{row.serviceAccessLevelId}</TableCell>
                <TableCell>{row.processEngineAccessLevelId}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.isEnabled ? 'Yes' : 'No'}</TableCell>
                <TableCell align='right'>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            setSelectedAccessLevel(row)
                            setEditAccessLevelModalOpen(true)
                        }}>
                        Edit
                    </Button>
                </TableCell>
            </TableRow>
        ))
    }

    return (
        <Paper className={classes.tableWrapper}>
            <div className={classes.tableSection}>
                <EnhancedTableToolbar />
                <TableContainer>
                    <Table
                        className={classes.table}
                        aria-labelledby='tableTitle'
                        aria-label='enhanced table'>
                        <TableHead className={globalClasses.tableHeader}>
                            <TableRow>
                                {headCells.map((headCell, index) => (
                                    <TableCell key={'head-cell-' + index}>
                                        {headCell.label}
                                    </TableCell>
                                ))}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>{getTableContent()}</TableBody>
                    </Table>
                </TableContainer>
            </div>

            {createAccessLevelModalOpen && (
                <CreateAccessLevelModal
                    data-testid='create-accesslevel-modal'
                    setModalOpen={isOpen =>
                        setCreateAccessLevelModalOpen(isOpen)
                    }
                    okMethod={createAccessLevelModalOkClickHandler}
                />
            )}

            {editAccessLevelModalOpen && (
                <EditAccessLevelModal
                    data-testid='edit-accesslevel-modal'
                    setModalOpen={isOpen => setEditAccessLevelModalOpen(isOpen)}
                    okMethod={editAccessLevelOkClickHandler}
                    accessLevel={selectedAccessLevel}
                />
            )}
        </Paper>
    )
}

export default AccessLevels
